import React, { useState, useEffect } from 'react'
import { X } from 'lucide-react'

interface WordCloudSearchProps {
  galleries: Record<string, any>
  onSearchResults: (results: Array<{ galleryId: string; gallery: any; matchingImages: any[] }>) => void
  onKeywordSearch?: (keywords: string[]) => void
}

export const WordCloudSearch: React.FC<WordCloudSearchProps> = ({ 
  galleries, 
  onSearchResults, 
  onKeywordSearch 
}) => {
  const [selectedKeywords, setSelectedKeywords] = useState<string[]>([])
  const [keywordCounts, setKeywordCounts] = useState<Record<string, number>>({})

  // Extract and count keywords from all galleries
  useEffect(() => {
    const counts: Record<string, number> = {}
    
    Object.entries(galleries).forEach(([, gallery]) => {
      if (gallery.keywords) {
        gallery.keywords.forEach((keyword: string) => {
          counts[keyword] = (counts[keyword] || 0) + 1
        })
      }
      
      if (gallery.images) {
        gallery.images.forEach((image: any) => {
          if (image.keywords) {
            image.keywords.forEach((keyword: string) => {
              counts[keyword] = (counts[keyword] || 0) + 1
            })
          }
        })
      }
    })
    
    setKeywordCounts(counts)
  }, [galleries])

  // Handle keyword selection
  const handleKeywordClick = (keyword: string) => {
    let newSelected: string[]
    
    if (selectedKeywords.includes(keyword)) {
      newSelected = selectedKeywords.filter(k => k !== keyword)
    } else {
      newSelected = [...selectedKeywords, keyword]
    }
    
    setSelectedKeywords(newSelected)
    
    // Use onKeywordSearch if provided (for new portfolio page), otherwise use traditional search
    if (onKeywordSearch) {
      onKeywordSearch(newSelected)
    } else {
      performSearch(newSelected)
    }
  }

  // Clear all selected keywords
  const clearSelection = () => {
    setSelectedKeywords([])
    
    // Use onKeywordSearch if provided, otherwise use traditional search
    if (onKeywordSearch) {
      onKeywordSearch([])
    } else {
      performSearch([])
    }
  }

  // Perform search based on selected keywords
  const performSearch = (keywords: string[]) => {
    if (keywords.length === 0) {
      // Show all galleries
      const allResults = Object.entries(galleries).map(([galleryId, gallery]) => ({
        galleryId,
        gallery,
        matchingImages: gallery.images || []
      }))
      onSearchResults(allResults)
      return
    }

    const results: Array<{ galleryId: string; gallery: any; matchingImages: any[] }> = []

    Object.entries(galleries).forEach(([galleryId, gallery]) => {
      const matchingImages: any[] = []
      
      // Check gallery-level keywords
      const galleryMatches = keywords.every(keyword =>
        gallery.keywords?.includes(keyword)
      )
      
      // Check image-level keywords
      if (gallery.images) {
        gallery.images.forEach((image: any) => {
          const imageMatches = keywords.every(keyword =>
            image.keywords?.includes(keyword) || gallery.keywords?.includes(keyword)
          )
          
          if (imageMatches) {
            matchingImages.push(image)
          }
        })
      }

      // Include gallery if it has matches at gallery level or has matching images
      if (galleryMatches || matchingImages.length > 0) {
        results.push({
          galleryId,
          gallery,
          matchingImages: matchingImages.length > 0 ? matchingImages : gallery.images || []
        })
      }
    })

    onSearchResults(results)
  }

  // Get font size based on keyword frequency
  const getFontSize = (count: number) => {
    const maxCount = Math.max(...Object.values(keywordCounts))
    const minSize = 12
    const maxSize = 24
    return minSize + ((count / maxCount) * (maxSize - minSize))
  }

  // Sort keywords by frequency
  const sortedKeywords = Object.entries(keywordCounts)
    .sort(([, a], [, b]) => b - a)
    .slice(0, 50) // Limit to top 50 keywords

  return (
    <div className="bg-gray-50 py-8 px-4 sm:px-6 lg:px-8 border-b border-gray-200">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-6">
          <h2 className="text-2xl font-light text-gray-900 mb-2">Search by Keywords</h2>
          <p className="text-sm text-gray-600">
            Click keywords to search. Multiple keywords will find images matching ALL selected terms.
          </p>
        </div>

        {/* Selected Keywords */}
        {selectedKeywords.length > 0 && (
          <div className="mb-6">
            <div className="flex items-center justify-center flex-wrap gap-2 mb-4">
              {selectedKeywords.map((keyword) => (
                <span
                  key={keyword}
                  className="inline-flex items-center px-3 py-1 bg-gray-900 text-white text-sm rounded-full"
                >
                  {keyword}
                  <button
                    onClick={() => handleKeywordClick(keyword)}
                    className="ml-2 hover:bg-gray-700 rounded-full p-0.5"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </span>
              ))}
            </div>
            <div className="text-center">
              <button
                onClick={clearSelection}
                className="text-sm text-gray-600 hover:text-gray-900 underline"
              >
                Clear all selections
              </button>
            </div>
          </div>
        )}

        {/* Word Cloud */}
        <div className="text-center leading-relaxed">
          {sortedKeywords.map(([keyword, count]) => (
            <button
              key={keyword}
              onClick={() => handleKeywordClick(keyword)}
              className={`inline-block mx-2 my-1 px-2 py-1 rounded transition-all duration-200 ${
                selectedKeywords.includes(keyword)
                  ? 'bg-gray-900 text-white'
                  : 'text-gray-700 hover:text-gray-900 hover:bg-gray-200'
              }`}
              style={{
                fontSize: `${getFontSize(count)}px`,
                fontWeight: selectedKeywords.includes(keyword) ? 600 : 400
              }}
              title={`${count} ${count === 1 ? 'match' : 'matches'}`}
            >
              {keyword}
            </button>
          ))}
        </div>
      </div>
    </div>
  )
}
